import {ChangeEvent, ClipboardEvent, KeyboardEvent, memo, useEffect, useRef, useState} from 'react';

import styled from 'styled-components';

import {body14Normal} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import _Input from '~/shared/components/Input';

const CODE_LENGTH = 5;

interface IVerificationCodeInput {
  onChange: (code: string) => void;
  onComplete?: (code: string) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const InputsWrapper = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  width: 100%;
  direction: ltr;
`;

const Input = styled(_Input)<{hasError?: boolean}>`
  width: 48px;
  height: 48px;
  ${body14Normal}
  text-align: center;
  border: 1px solid ${({theme, hasError}) => (hasError ? theme.colors.contentError : theme.colors.borderStrong)};
  border-radius: 12px;
  color: ${({theme}) => theme.colors.secondary};
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({theme, hasError}) => (hasError ? theme.colors.contentError : theme.colors.borderForm)};
  }
`;

const ErrorText = styled.div`
  ${body14Normal}
  line-height: 20px;
  min-height: 20px;
  color: ${({theme}) => theme.colors.contentError};
  text-align: center;
  margin-bottom: 8px;
`;

const VerificationCodeInput = ({
  onChange,
  onComplete,
  error,
  disabled,
  className,
}: IVerificationCodeInput) => {
  const {t} = getLocalizationService();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(CODE_LENGTH).fill(null));

  useEffect(() => {
    if (error) {
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
  }, [error]);

  const updateCode = (newDigits: string[]) => {
    setDigits(newDigits);
    const code = newDigits.join('');
    onChange(code);
    
    if (code.length === CODE_LENGTH && onComplete) {
      onComplete(code);
    }
  };

  const handleInputChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const newDigit = inputValue.replace(/\D/g, '').slice(-1);
    
    const newDigits = [...digits];
    newDigits[index] = newDigit;
    updateCode(newDigits);

    if (newDigit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      const newDigits = [...digits];
      
      if (digits[index]) {
        newDigits[index] = '';
        updateCode(newDigits);
        event.preventDefault();
        return;
      }
      
      if (index > 0) {
        newDigits[index - 1] = '';
        updateCode(newDigits);
        inputRefs.current[index - 1]?.focus();
      }
      
      event.preventDefault();
      return;
    }
    
    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const digitsOnly = pastedData.replace(/\D/g, '');
    const pastedCodeLengthChars = digitsOnly.slice(0, CODE_LENGTH).split('').concat(Array(CODE_LENGTH).fill('')).slice(0, CODE_LENGTH);

    updateCode(pastedCodeLengthChars);

    const nextEmptyIndex = pastedCodeLengthChars.findIndex(d => !d);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
      return;
    }
    inputRefs.current[CODE_LENGTH - 1]?.focus();
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  return (
    <Container className={className} data-test-id="verificationCodeInput">
      <InputsWrapper>
        {digits.map((digit, index) => (
          <Input
            key={`digit_${index + 1}`}
            name={`verification-code-${index}`}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            inputType="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            value={digit}
            onChange={handleInputChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={disabled}
            hasError={Boolean(error)}
            maxLength={1}
            aria-label={t('verification_code_digit', {digit: index + 1, total: CODE_LENGTH})}
          />
        ))}
      </InputsWrapper>
      <ErrorText>{error}</ErrorText>
    </Container>
  );
};

export default memo(VerificationCodeInput);
